import dayjs from 'dayjs';
import {
  AthletePackageList,
  AthletePackageRetrieve,
  BaseCreditDispensation,
  CreditDispensation,
} from 'types/generated';

import { parseDateTimeToDate } from './date-functions';

export const getCreditDispensationsTotalCredits = (
  creditDispensations: CreditDispensation[] | BaseCreditDispensation[],
): number => {
  return creditDispensations.reduce((acc, curr) => acc + (curr.credits || 0), 0);
};

/**
 * The credits dispensed together on each dispense date, summed across rules. Rollovers are left out.
 */
export const getPooledCreditsByDispenseDate = (
  creditDispensations: CreditDispensation[],
): Record<string, number> => {
  const byDispenseDate: Record<string, CreditDispensation[]> = {};
  creditDispensations
    .filter(creditDispensation => !creditDispensation.isRollover)
    .forEach(creditDispensation => {
      byDispenseDate[creditDispensation.dispenseDate] = [
        ...(byDispenseDate[creditDispensation.dispenseDate] || []),
        creditDispensation,
      ];
    });
  return Object.fromEntries(
    Object.entries(byDispenseDate).map(([dispenseDate, dispensations]) => [
      dispenseDate,
      getCreditDispensationsTotalCredits(dispensations),
    ]),
  );
};

export const getCreditDispensationsRemainingCredits = (
  creditDispensations: CreditDispensation[],
): number => {
  const total = getCreditDispensationsTotalCredits(creditDispensations);
  const used = creditDispensations.reduce((acc, curr) => acc + curr.creditsUsed, 0);

  return total - used;
};

export const formatAthletePackageValidityDisplay = (
  packageData:
    | Pick<
        AthletePackageRetrieve,
        'startDate' | 'endDate' | 'creditDispensations' | 'package' | 'cancellationDate'
      >
    | Pick<
        AthletePackageList,
        'startDate' | 'endDate' | 'creditDispensations' | 'package' | 'cancellationDate'
      >,
  dateFormat: string,
  separator?: string,
  timezone?: string,
): string => {
  const separatorSign = separator ?? '-';

  const startDate = dayjs(parseDateTimeToDate(packageData.startDate, timezone)).format(dateFormat);

  if (packageData.package.type === 'TIME_BASED' && packageData.endDate) {
    const endDate = dayjs(
      parseDateTimeToDate(packageData.cancellationDate || packageData.endDate, timezone),
    ).format(dateFormat);
    return `${startDate}${separatorSign}${endDate}`;
  } else if (packageData.package.type === 'TIME_OR_CREDIT_BASED' && packageData.endDate) {
    const endDate = dayjs(
      parseDateTimeToDate(packageData.cancellationDate || packageData.endDate, timezone),
    ).format(dateFormat);
    const dateRange = `${startDate}${separatorSign}${endDate}`;
    const limitedCredits = getCreditDispensationsTotalCredits(packageData.creditDispensations);

    return limitedCredits ? `${dateRange} or ${limitedCredits} Credits` : dateRange;
  } else {
    const totalCredits = getCreditDispensationsTotalCredits(packageData.creditDispensations);

    if (!totalCredits) {
      return '-';
    }
    return `${startDate}${separatorSign}${totalCredits} Credits`;
  }
};
